import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CONSENT_BANNER_VERSION,
  CONSENT_LIFETIME_MONTHS,
  CONSENT_STORAGE_KEY,
} from '../data/consent.config';
import { isSupportedLang } from '../data/languages.config';
import type { ConsentChange, ConsentChoice, StoredConsent } from '../models/consent.model';
import { LanguageService } from './language.service';

/**
 * Owns the visitor's answer to the audience-measurement banner.
 *
 * It loads nothing itself: the analytics facade reads `granted()` when it starts and
 * follows `changes` afterwards. On the server, and in a build with no
 * measurement ID, it is inert: no choice, no banner, no storage access.
 *
 * **Refusal always applies; acceptance only applies once it is stored.** If storage
 * throws, the banner asks again on every page load and nothing loads.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly language = inject(LanguageService);

  /** The build has a measurement ID. True during the prerender too, so markup that depends on it hydrates unchanged. */
  readonly configured = environment.analytics.measurementId !== '';
  readonly enabled = this.configured && isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _stored = signal<StoredConsent | null>(null);
  /** The answer in effect, with its date; `null` until the visitor answers. */
  readonly stored = this._stored.asReadonly();
  readonly choice = computed<ConsentChoice | null>(() => this._stored()?.choice ?? null);
  readonly granted = computed(() => this.choice() === 'granted');

  private readonly _lapsed = signal(false);
  /** True when a stored answer was dropped at start-up, so the facade clears GA's cookies. */
  readonly lapsed = this._lapsed.asReadonly();

  private readonly changeEvents = new Subject<ConsentChange>();
  /** Emits only when the choice in effect changes, never on a repeated answer. */
  readonly changes = this.changeEvents.asObservable();

  private readonly _reopened = signal(false);
  private readonly _reopenRequests = signal(0);
  /** Counts "Cookie settings" presses, so the banner refocuses even when it is already open. */
  readonly reopenRequests = this._reopenRequests.asReadonly();

  private readonly answeredUnsaved = signal(false);

  readonly bannerOpen = computed(
    () =>
      this.enabled &&
      (this._reopened() || (this._stored() === null && !this.answeredUnsaved())),
  );

  constructor() {
    if (!this.enabled) return;

    this.restoreStoredAnswer();
    window.addEventListener('storage', (event) => {
      if (event.key === CONSENT_STORAGE_KEY || event.key === null) this.followOtherTab();
    });
  }

  accept(): void {
    this.answer('granted');
  }

  refuse(): void {
    this.answer('denied');
  }

  /** "Cookie settings": shows the banner again, over the answer in effect. */
  reopen(): void {
    if (!this.enabled) return;

    this._reopened.set(true);
    this._reopenRequests.update((count) => count + 1);
  }

  /** Closes a reopened banner and keeps the answer in effect. A first visit cannot be dismissed. */
  dismiss(): void {
    if (this._stored() !== null) this._reopened.set(false);
  }

  private answer(choice: ConsentChoice): void {
    if (!this.enabled) return;

    const previous = this.choice();
    const record: StoredConsent = {
      choice,
      version: CONSENT_BANNER_VERSION,
      at: new Date().toISOString(),
      lang: this.language.lang(),
    };
    const saved = this.write(record);
    this._reopened.set(false);

    if (!saved && choice === 'granted') {
      this.answeredUnsaved.set(true);
      return;
    }

    this._stored.set(record);
    const change = changeBetween(previous, choice);
    if (change) this.changeEvents.next(change);
  }

  /** An answer given in another tab applies here too, so a withdrawal stops measurement everywhere. */
  private followOtherTab(): void {
    const previous = this.choice();
    const record = this.read();
    const next = record && isCurrent(record) ? record : null;
    this._stored.set(next);

    const change = next
      ? changeBetween(previous, next.choice)
      : previous === 'granted'
        ? 'withdrawn'
        : null;
    if (change) this.changeEvents.next(change);
  }

  private restoreStoredAnswer(): void {
    const record = this.read();
    if (!record) return;

    if (isCurrent(record)) {
      this._stored.set(record);
      return;
    }

    this._lapsed.set(true);
    this.forget();
  }

  private read(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
      return raw === null ? null : parseStoredConsent(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private write(record: StoredConsent): boolean {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
      return true;
    } catch {
      this.forget();
      return false;
    }
  }

  private forget(): void {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      /* storage unavailable — there is nothing to remove */
    }
  }
}

function changeBetween(previous: ConsentChoice | null, next: ConsentChoice): ConsentChange | null {
  if (previous === next) return null;
  if (next === 'granted') return 'granted';
  return previous === 'granted' ? 'withdrawn' : 'denied';
}

function isCurrent(record: StoredConsent): boolean {
  const answered = new Date(record.at);
  const expires = new Date(answered);
  expires.setMonth(expires.getMonth() + CONSENT_LIFETIME_MONTHS);
  // 31 August plus six months would roll into March; clamp to the month's last day.
  if (expires.getDate() !== answered.getDate()) expires.setDate(0);

  return (
    record.version === CONSENT_BANNER_VERSION &&
    answered.getTime() <= Date.now() &&
    Date.now() < expires.getTime()
  );
}

function parseStoredConsent(value: unknown): StoredConsent | null {
  if (typeof value !== 'object' || value === null) return null;

  const { choice, version, at, lang } = value as Record<string, unknown>;
  const valid =
    (choice === 'granted' || choice === 'denied') &&
    typeof version === 'number' &&
    typeof at === 'string' &&
    !Number.isNaN(Date.parse(at));
  if (!valid) return null;

  // Records written before `lang` was kept stay valid without it.
  return typeof lang === 'string' && isSupportedLang(lang)
    ? { choice, version, at, lang }
    : { choice, version, at };
}
